/**
 * TwapOrderModal — splits a large position entry into N equal market
 * sub-orders executed at a fixed time interval (client-side execution,
 * tab must stay open).
 */
import React, { useState, useRef, useCallback, useEffect } from "react";
import { t, Trans } from "@lingui/macro";
import { ethers, ZeroAddress } from "ethers";

import Modal from "components/Modal/Modal";
import { USD_DECIMALS } from "config/factors";
import { getContract } from "config/contracts";
import { getConstant } from "config/chains";
import { callContract } from "lib/contracts";
import { parseValue, formatAmount } from "lib/numbers";
import { helperToast } from "lib/helperToast";
import { replaceNativeTokenAddress } from "domain/tokens";
import PositionRouter from "sdk/abis/PositionRouter.json";

import "./TwapOrderModal.css";

const INTERVAL_OPTIONS = [
  { label: "1 min", seconds: 60 },
  { label: "5 min", seconds: 300 },
  { label: "10 min", seconds: 600 },
  { label: "15 min", seconds: 900 },
  { label: "30 min", seconds: 1800 },
  { label: "1 hr", seconds: 3600 },
];

type Props = {
  isVisible: boolean;
  onClose: () => void;
  chainId: number;
  signer: any;
  account: string | undefined;
  fromTokenAddress: string;
  indexTokenAddress: string;
  collateralTokenAddress: string;
  nativeTokenAddress: string;
  isLong: boolean;
  isSynthetic: boolean;
  minExecutionFee: bigint;
  setPendingTxns?: (txns: any) => void;
  payTokenSymbol?: string;
};

export function TwapOrderModal({
  isVisible,
  onClose,
  chainId,
  signer,
  account,
  fromTokenAddress,
  indexTokenAddress,
  collateralTokenAddress,
  nativeTokenAddress,
  isLong,
  isSynthetic,
  minExecutionFee,
  setPendingTxns,
  payTokenSymbol,
}: Props) {
  const [totalCollateral, setTotalCollateral] = useState("");
  const [numParts, setNumParts] = useState("5");
  const [intervalIdx, setIntervalIdx] = useState(1); // default 5min
  const [isRunning, setIsRunning] = useState(false);
  const [partsCompleted, setPartsCompleted] = useState(0);
  const [totalParts, setTotalParts] = useState(0);
  const [nextRunIn, setNextRunIn] = useState(0);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const partsRef = useRef({ completed: 0, total: 0 });

  const stopAll = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    timerRef.current = null;
    countdownRef.current = null;
    setIsRunning(false);
  }, []);

  useEffect(() => {
    if (!isVisible) stopAll();
  }, [isVisible, stopAll]);

  const submitOnePart = useCallback(
    async (collateralPerPart: bigint, partNum: number) => {
      if (!account || !signer) return;

      const path =
        fromTokenAddress === ZeroAddress
          ? [nativeTokenAddress, collateralTokenAddress]
          : fromTokenAddress === collateralTokenAddress
          ? [fromTokenAddress]
          : [fromTokenAddress, collateralTokenAddress];

      const finalPath = replaceNativeTokenAddress(path, nativeTokenAddress);
      const priceLimit = isLong ? 0n : ethers.MaxUint256;

      const routerKey = isSynthetic ? "SynthPositionRouter" : "PositionRouter";
      const routerAddr = getContract(chainId, routerKey);
      const contract = new ethers.Contract(routerAddr, PositionRouter.abi, signer);

      const fromETH = fromTokenAddress === ZeroAddress;
      const method = fromETH ? "createIncreasePositionETH" : "createIncreasePosition";
      const sizeDelta = collateralPerPart * 1n; // 1x leverage for TWAP (collateral = size for simplicity)
      const params = fromETH
        ? [
            [finalPath, indexTokenAddress, 0, sizeDelta, isLong, priceLimit, minExecutionFee, ethers.ZeroHash, ZeroAddress],
          ]
        : [
            finalPath,
            indexTokenAddress,
            collateralPerPart,
            0,
            sizeDelta,
            isLong,
            priceLimit,
            minExecutionFee,
            ethers.ZeroHash,
            ZeroAddress,
          ];

      const value = fromETH ? collateralPerPart + minExecutionFee : minExecutionFee;

      try {
        await callContract(chainId, contract, method, params, {
          value,
          setPendingTxns,
          sentMsg: t`TWAP part ${partNum}/${partsRef.current.total} submitted`,
          failMsg: t`TWAP part ${partNum} failed`,
          hideSuccessMsg: true,
        });
        partsRef.current.completed++;
        setPartsCompleted(partsRef.current.completed);
      } catch {
        helperToast.error(t`TWAP part ${partNum} failed — execution paused`);
        stopAll();
      }
    },
    [
      account,
      signer,
      chainId,
      fromTokenAddress,
      indexTokenAddress,
      collateralTokenAddress,
      nativeTokenAddress,
      isLong,
      isSynthetic,
      minExecutionFee,
      setPendingTxns,
      stopAll,
    ]
  );

  const handleStart = useCallback(async () => {
    const n = parseInt(numParts, 10);
    if (!n || n < 2 || n > 20) return helperToast.error(t`Parts must be 2–20`);
    const total = parseValue(totalCollateral, 18) ?? 0n;
    if (total <= 0n) return helperToast.error(t`Enter a valid collateral amount`);
    if (!account || !signer) return;

    const perPart = total / BigInt(n);
    const intervalSec = INTERVAL_OPTIONS[intervalIdx].seconds;

    partsRef.current = { completed: 0, total: n };
    setTotalParts(n);
    setPartsCompleted(0);
    setIsRunning(true);
    setNextRunIn(intervalSec);

    // Submit first part immediately
    await submitOnePart(perPart, 1);
    if (partsRef.current.completed === 0) return; // first part failed

    if (n === 1) {
      setIsRunning(false);
      return;
    }

    // Countdown display
    let remaining = intervalSec;
    countdownRef.current = setInterval(() => {
      remaining--;
      setNextRunIn(remaining);
      if (remaining <= 0) remaining = intervalSec;
    }, 1000);

    // Subsequent parts
    let partNum = 2;
    timerRef.current = setInterval(async () => {
      if (partNum > n) {
        stopAll();
        helperToast.success(t`TWAP complete — all ${n} parts executed`);
        return;
      }
      await submitOnePart(perPart, partNum);
      partNum++;
      remaining = intervalSec;
    }, intervalSec * 1000);
  }, [numParts, totalCollateral, intervalIdx, account, signer, submitOnePart, stopAll]);

  const handleCancel = useCallback(() => {
    stopAll();
    setPartsCompleted(0);
    setTotalParts(0);
  }, [stopAll]);

  const progress = totalParts > 0 ? (partsCompleted / totalParts) * 100 : 0;

  return (
    <Modal isVisible={isVisible} setIsVisible={onClose} label={t`TWAP Order`}>
      <div className="TM-root">
        <p className="TM-desc">
          <Trans>
            Splits your entry into equal market orders executed at a fixed interval.
            Keep this tab open — execution stops if you close it.
          </Trans>
        </p>

        <div className="TM-field">
          <label className="TM-label"><Trans>Amount to split</Trans></label>
          <div className="TM-input-wrap">
            <input
              className="TM-input"
              type="number"
              placeholder="0.0"
              value={totalCollateral}
              onChange={(e) => setTotalCollateral(e.target.value)}
              disabled={isRunning}
            />
            <span className="TM-input-token">{payTokenSymbol || t`tokens`}</span>
          </div>
        </div>

        <div className="TM-row">
          <div className="TM-field TM-field-half">
            <label className="TM-label"><Trans>Number of parts</Trans></label>
            <input
              className="TM-input"
              type="number"
              min={2}
              max={20}
              value={numParts}
              onChange={(e) => setNumParts(e.target.value)}
              disabled={isRunning}
            />
          </div>
          <div className="TM-field TM-field-half">
            <label className="TM-label"><Trans>Interval</Trans></label>
            <select
              className="TM-select"
              value={intervalIdx}
              onChange={(e) => setIntervalIdx(Number(e.target.value))}
              disabled={isRunning}
            >
              {INTERVAL_OPTIONS.map((o, i) => (
                <option key={i} value={i}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {totalCollateral && numParts && !isNaN(parseFloat(totalCollateral)) && parseInt(numParts) >= 2 && (
          <div className="TM-summary">
            <div className="TM-summary-item">
              <span className="TM-summary-label"><Trans>Each order</Trans></span>
              <span className="TM-summary-value">
                {(parseFloat(totalCollateral) / parseInt(numParts)).toFixed(4)} {payTokenSymbol || ""}
              </span>
            </div>
            <div className="TM-summary-item">
              <span className="TM-summary-label"><Trans>Schedule</Trans></span>
              <span className="TM-summary-value">
                {parseInt(numParts)} × {INTERVAL_OPTIONS[intervalIdx].label}
              </span>
            </div>
            <div className="TM-summary-item">
              <span className="TM-summary-label"><Trans>Duration</Trans></span>
              <span className="TM-summary-value">
                ~{((parseInt(numParts) - 1) * INTERVAL_OPTIONS[intervalIdx].seconds / 60).toFixed(0)} min
              </span>
            </div>
          </div>
        )}

        {isRunning && (
          <div className="TM-progress-section">
            <div className="TM-progress-bar-track">
              <div className="TM-progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="TM-progress-text">
              <Trans>{partsCompleted} / {totalParts} executed</Trans>
              {partsCompleted < totalParts && (
                <span> — <Trans>next in {nextRunIn}s</Trans></span>
              )}
            </div>
          </div>
        )}

        <div className="TM-actions">
          {!isRunning ? (
            <button className="TM-btn-start" onClick={handleStart}>
              <Trans>Start TWAP</Trans>
            </button>
          ) : (
            <button className="TM-btn-cancel" onClick={handleCancel}>
              <Trans>Cancel TWAP</Trans>
            </button>
          )}
          <button className="TM-btn-close" onClick={onClose} disabled={isRunning}>
            <Trans>Close</Trans>
          </button>
        </div>
      </div>
    </Modal>
  );
}
